<template>
	<div id="rootApp" class="polly-app" :class="{ 'is-pwa': isHomeScreenPWA }">
		<popup-modal
			id="rootPopupModal"
			ref="rootPopupModal"
			:type="modalType"
			:title="modalTitle"
			:message="modalMessage"
			:content-class="modalContentClass"
			:primary-button-text="modalPrimaryButtonText"
			:secondary-button-text="modalSecondaryButtonText"
			@clickPrimary="rootPopupClickPrimary"
			@clickSecondary="rootPopupClickSecondary"
		>
		</popup-modal>
		<!-- No header: Polly has Athena instead. Only in mock mode a small red "M" says that nothing here is real. -->
		<mock-reset-button v-if="config.mockBackend" />
		<router-view v-slot="{ Component }">
			<transition :name="transitionName">
				<component :is="Component" id="appContent" class="router-view container-lg polly-route" :class="{ 'polly-stage-route': isStageRoute }" />
			</transition>
		</router-view>
		<mobile-debug-log v-if="showMobileDebugLog" ref="mobileDebugLogRef"></mobile-debug-log>
		<!-- Gives Safari's bottom toolbar its colour in a browser tab, see the style below -->
		<div class="polly-toolbar-tint" aria-hidden="true"></div>
		<!-- Keeps the large iOS 27 blur off the pages without Athena's fixed header, see the style below -->
		<div v-if="!isStageRoute" class="polly-top-edge-bar" aria-hidden="true"></div>
	</div>
</template>

<script>
/**
 * The root of the Polly app in the DOM tree - LIQUIDO's root-app.vue without the header and without the team.
 *
 * It renders the routed page with its slide transition and the global popup for infos and errors. The helpers every
 * page reaches as this.$root.<name> - showError(), scrollToTop(), isHomeScreenPWA, ... - are the ones LIQUIDO has too,
 * see src/shared/shell/app-shell.js. What is Polly's own is here: no header, the backend's limits through the polly
 * client, and no login to send anybody to.
 */
import popupModal from "@/shared/components/popup-modal.vue"
import mobileDebugLog from "@/shared/components/mobile-debug-log.vue"
import mockResetButton from "@/polly/components/mock-reset-button.vue"
import pollyApi from "@/polly/services/polly-client.js"
import { createAppShell } from "@/shared/shell/app-shell.js"
import { loadBackendConfig } from "@/shared/shell/backend-config.js"

/**
 * Pages slide from right to left in this order (see app-shell.js): a new polly, my pollys on the right of it, then a
 * polly; the confirm page comes from the link mail and goes on to my pollys, which slides back.
 */
const pageOrder = {
	"createPolly": 3,
	"pollys": 4,
	"showPolly": 6,
	"confirmPollyEmail": 7,
	"pollyLogin": 2,   // an expired session: login first, then on (forward) to where the user was going
	// Legal pages, only linked from the bottom of my pollys
	"impressum": 20,
	"agb": 20,
	"datenschutz": 20,
	"pageNotFound": 30,
}

export default {
	name: "PollyApp",
	i18n: {
		messages: {
			en: { BackendNotReachable: "I cannot reach the Polly server right now. Please check your internet connection." },
			de: { BackendNotReachable: "Ich kann den Polly Server gerade nicht erreichen. Bitte prüfe, ob du online bist." },
		},
	},
	mixins: [createAppShell({ pageOrder })],
	components: { popupModal, mobileDebugLog, mockResetButton },
	computed: {
		/** The routes of polly-page.vue: it brings its own fixed header (Athena) */
		isStageRoute() {
			return ["createPolly", "showPolly"].includes(this.$route?.name)
		},
	},
	mounted() {
		loadBackendConfig(() => pollyApi.getConfig())
			.then(loaded => { if (!loaded) this.showWarning(this.$t("BackendNotReachable")) })
	},
}
</script>

<style>
/* The screen layout (#rootApp is the screen and scrolls, header and footer are fixed) is the same as LIQUIDO's: liquido.css. */

/* In a Safari tab the area behind the floating bottom toolbar takes the colour of a fixed element at the bottom edge (POC README §2.6),
   and the footer is transparent now: without this strip it is white. The colour is the bottom of --app-gradient. Not in the installed app.
   A real element that takes taps: Safari finds it by a hit test, which skips pointer-events: none and reports a ::after as its parent.
   It stays below the button, which keeps var(--unit) from the bottom edge. */
.polly-toolbar-tint {
	display: none;
}
@media (display-mode: browser) {
	.polly-toolbar-tint {
		display: block;
		position: fixed;
		left: 0;
		right: 0;
		bottom: 0;
		height: 8px;
		background-color: #dbe8f6;
		z-index: 998;   /* under the footer (999) */
	}
}

/* A thin bar in the page's colour, fixed at the top edge, on every page without Athena's fixed header (My Pollys, the confirm page,
   404, the legal pages). An installed iOS 27 app lays its large blur over the top of a page that has no fixed box at the top edge,
   and a 4px one is enough - tested on an iPhone with LIQUIDO's start page (.top-edge-bar in welcome-chat-v2.vue). A real element. */
.polly-top-edge-bar {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	height: 4px;
	background-color: #f4f9fe;   /* the light blue of Polly's page, = the html background and theme-color (index.html) */
	z-index: 1000;
}

/* Every heading in Polly is in Polly's rounded sans-serif, only the question of a polly keeps the serif font (polly-card.vue,
   polly-page.vue). liquido.css makes every #app h1-h6 and .page-title serif, and ids beat the components' classes, so it is said here. */
#app h1, #app h2, #app h3, #app h4, #app h5, #app h6,
#app .page-title {
	font-family: var(--polly-round, ui-rounded, "SF Pro Rounded", var(--sans-serif-font));
}
#app .polly-question {
	font-family: var(--serif-font);
}

/* Polly has no header: the page starts right under the top of the screen (the notch on an iPhone).
   At the bottom every page keeps clear of the fixed footer (polly-footer.vue), which takes no room itself. */
#app #appContent.polly-route {
	padding-top: calc(env(safe-area-inset-top) + var(--unit) + 0.35rem);
	padding-bottom: calc(5.5rem + env(safe-area-inset-bottom));
}

/** The polly screen (polly-page.vue): it brings its own fixed header (Athena) and the room under it, so #appContent has no padding at the top
    and is a flex column, which lets the screen fill the viewport. Its footer has the one big button, so it has room at the bottom. */
#app #appContent.polly-stage-route {
	display: flex;
	flex-direction: column;
	padding-top: 0;
	padding-bottom: calc(5.5rem + env(safe-area-inset-bottom));
}

/* The big round button of every page's footer (polly-footer.vue), and of the modal in polly-page.vue.
   The colours fall back to Polly's palette, which polly-page.vue declares on its own root. */
.polly-cta {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	width: 100%;
	min-height: 56px;
	padding: 0 1rem;
	border: 0;
	border-radius: 999px;
	background: var(--polly-orange, #f2a541);
	color: var(--primary);
	font: 700 1.1rem var(--polly-round, ui-rounded, "SF Pro Rounded", var(--sans-serif-font));
	box-shadow: 0 4px 0 var(--polly-orange-deep, #c9791a), 0 8px 18px rgba(30, 58, 95, 0.35);   /* the edge, and a soft shadow: the button is raised above the page scrolling behind it */
	transition: transform 0.08s, box-shadow 0.08s;
}
a.polly-cta {
	text-decoration: none;
}
.polly-cta:active {
	transform: translateY(3px);
	box-shadow: 0 1px 0 var(--polly-orange-deep, #c9791a), 0 2px 6px rgba(30, 58, 95, 0.3);
}
.polly-cta:disabled {   /* while the vote is on its way */
	opacity: 0.7;
	cursor: default;
}
.polly-cta.not-ready {   /* tappable, so a tap can say what is missing - just not shouting */
	background: #cfd2d8;   /* a neutral grey: neither the paper's beige nor the page's blue */
	color: #4a4f5a;
	box-shadow: 0 4px 0 #aeb2ba, 0 8px 18px rgba(30, 58, 95, 0.25);
}
.polly-cta:focus-visible {
	outline: 3px solid var(--polly-orange-deep, #c9791a);
	outline-offset: 2px;
}
</style>
